// src/pages/DebtStudio/components/Portfolios/fileManager.js
import { useMemo, useCallback } from "react";
import useFileManager from "components/FileManager/hooks/useFileManager";
import { useProfile } from "context/ProfileContext";
import { supabase } from "services/supabaseClient";
import {
  getDebtStudioPermissions,
  normalizeSharing,
} from "lib/permissions";

/* =============================================================================
   Portfolios File Manager (Adapter + Modal View)
============================================================================= */

/* -----------------------------------------------------------------------------
 * Adapter hook: usePortfoliosFileManager
 * -------------------------------------------------------------------------- */

export function usePortfoliosFileManager({ limit = 100 } = {}) {
  const { profile, selectedOrg } = useProfile();

  const {
    records: rawRecords,
    recents,
    loading,
    error,
    fetchRecords,
    addToRecents,
  } = useFileManager({
    table: "ds_portfolios",
    org: selectedOrg,
    limit,
    select: "*",
  });

  const records = useMemo(() => {
    if (!Array.isArray(rawRecords)) return [];
    return rawRecords.map((r) => {
      const permissions = getDebtStudioPermissions(r, { id: profile?.id });
      return {
        ...r,
        sharing: normalizeSharing(r?.sharing),
        ...permissions,
      };
    });
  }, [rawRecords, profile?.id]);

  const openRecord = useCallback(
    (portfolio) => {
      addToRecents(portfolio);
    },
    [addToRecents]
  );

  // Owner-only delete (RLS enforced), memberships cascade via FK
  const deletePortfolio = useCallback(
    async (portfolioId) => {
      if (!portfolioId) throw new Error("Invalid delete request.");
      if (!profile?.id) throw new Error("User not authenticated.");

      const portfolio = records.find((r) => r.id === portfolioId);
      if (!portfolio) throw new Error("Portfolio not found.");
      if (!portfolio.isOwner) throw new Error("You do not own this portfolio.");

      const { error: delErr } = await supabase
        .from("ds_portfolios")
        .delete()
        .eq("id", portfolioId);

      if (delErr) throw delErr;

      await fetchRecords();
    },
    [records, profile?.id, fetchRecords]
  );

  const hasAccessError = useMemo(() => {
    if (!selectedOrg) return false;
    if (loading || error) return false;
    return Array.isArray(records) && records.length === 0;
  }, [selectedOrg, loading, error, records]);

  const isReadOnlyRecord = useCallback(
    (r) => {
      if (!r) return false;
      if (typeof r.isReadOnly === "boolean") return r.isReadOnly;
      return getDebtStudioPermissions(r, { id: profile?.id }).isReadOnly;
    },
    [profile?.id]
  );

  return {
    selectedOrg,

    records,
    recents,
    loading,
    error,

    refresh: fetchRecords,
    deleteRecord: deletePortfolio,
    openRecord,

    isReadOnlyRecord,

    hasAccessError,

    // NEW: FileRibbon label for the "+ New" action
    newLabel: "New Portfolio",
  };
}

/* -----------------------------------------------------------------------------
 * View config: portfoliosOpenView
 * -------------------------------------------------------------------------- */

function normStr(x) {
  return String(x ?? "").trim();
}

function safeText(x) {
  return typeof x === "string" ? x : "";
}

function safeDateISO(x) {
  return typeof x === "string" ? x : "";
}

function formatISODate(iso) {
  const s = safeDateISO(iso);
  return s ? s.slice(0, 10) : "";
}

function formatSharing(value) {
  const sharing = normalizeSharing(value);
  if (sharing === "org-edit") return "Org Edit";
  if (sharing === "org-view") return "Org View";
  return "User Only";
}

export const portfoliosOpenView = {
  title: "Open Saved Portfolio",
  defaultSort: { key: "name", asc: true },

  search: {
    placeholder: "Search portfolios…",
    predicate: (r, q) => {
      const name = normStr(safeText(r?.name)).toLowerCase();
      const desc = normStr(safeText(r?.description)).toLowerCase();
      const periodEnd = normStr(formatISODate(r?.period_end)).toLowerCase();
      const sharing = normStr(formatSharing(r?.sharing)).toLowerCase();
      return (
        name.includes(q) ||
        desc.includes(q) ||
        periodEnd.includes(q) ||
        sharing.includes(q)
      );
    },
  },

  selectedLabel: (r) => {
    const name = normStr(safeText(r?.name));
    return name || "(Untitled)";
  },

  columns: [
    {
      key: "name",
      header: "Portfolio Name",
      align: "left",
      showReadOnlyBadge: true,
      sortValue: (r) => normStr(safeText(r?.name)).toLowerCase(),
      render: (r) => {
        const name = safeText(r?.name) || "(Untitled)";
        return (
          <div className="truncate" title={name}>
            {name}
          </div>
        );
      },
    },
    {
      key: "description",
      header: "Description",
      align: "left",
      sortValue: (r) => normStr(safeText(r?.description)).toLowerCase(),
      render: (r) => {
        const d = safeText(r?.description);
        return d ? (
          <div className="truncate" title={d}>
            {d}
          </div>
        ) : (
          <span className="text-muted">—</span>
        );
      },
    },
    {
      key: "period_end",
      header: "Period End",
      align: "left",
      defaultAsc: false,
      sortValue: (r) => safeDateISO(r?.period_end) || "",
      render: (r) => {
        const d = formatISODate(r?.period_end);
        return d ? <span>{d}</span> : <span className="text-muted">—</span>;
      },
    },
    {
      key: "sharing",
      header: "Sharing",
      align: "left",
      sortValue: (r) => normalizeSharing(r?.sharing),
      render: (r) => <span>{formatSharing(r?.sharing)}</span>,
    },
  ],
};
